/**
 * Access-link service — the two calls the unlock page makes.
 *
 * Both are POST so the token rides in the body rather than the URL, keeping it out of access
 * logs, proxy logs and `Referer` headers.
 *
 * `skipAuth` on both: this page has no session by design, and the API mounts these routes
 * above the tenant middleware precisely because a browser arriving from a text message can
 * set no headers.
 *
 * @see /apps/api/src/api/public/v1/routes/accessLinkRoutes.ts
 */

import { apiClient } from '~/lib/apiClient';
import type { ApiResult } from '~/lib/apiClient/types';
import type { LinkAccessResponse, UnlockDoorResponse } from '../types';

/**
 * A refused access-link call, carrying the HTTP status the caller needs to tell apart.
 *
 * The message stays deliberately undifferentiated (see `unwrap`); the STATUS does not, because
 * "your link is not valid" and "you are going too fast" call for different copy and the second
 * is not a dead end.
 */
export class AccessLinkError extends Error {
  // A plain field, not a constructor parameter property — this app runs
  // `erasableSyntaxOnly`, under which parameter properties are a compile error.
  readonly httpStatus: number;

  constructor(message: string, httpStatus: number) {
    super(message);
    this.name = 'AccessLinkError';
    this.httpStatus = httpStatus;
    Object.setPrototypeOf(this, AccessLinkError.prototype);
  }

  /**
   * Did the request never reach a server at all?
   *
   * `apiClient` reports `httpStatus: 0` whenever no HTTP response arrived — a network error, a
   * timeout, or a body it could not parse (`apiClient.ts` builds the result with
   * `apiError.status ?? 0`, and `ApiError.status` is only set when a response existed).
   *
   * Two things follow, and both matter on this page:
   *
   * 1. It is a FAULT, not a refusal. The commonest trigger is mobile data outside a building,
   *    which is exactly where this page lives.
   * 2. **`message` must not be shown.** On this path `apiClient` fills it from the raw fetch
   *    error — `"Failed to fetch"` — because it passes `apiError.message` and drops the
   *    `userMessage` written for this case. That is platform-wide behaviour (every
   *    `throw new Error(result.message)` in this app inherits it), so we do not trust the
   *    string rather than trying to fix it here.
   */
  get isTransportFailure(): boolean {
    return this.httpStatus === 0;
  }

  /**
   * Did the server fail, as opposed to refusing the link?
   *
   * Worth telling apart. A refusal is final and the copy should say so; a fault is transient
   * and the member should be invited to try again rather than told their link is dead —
   * which matters more now that a DB error answers 500 instead of hanging the socket.
   */
  get isServerFault(): boolean {
    return this.isTransportFailure || this.httpStatus >= 500;
  }

  /**
   * The server's own words, or nothing.
   *
   * A refusal carries deliberately generic member-safe copy from the envelope and is worth
   * rendering. A transport failure carries `"Failed to fetch"` and never is.
   */
  get serverMessage(): string | undefined {
    return this.isTransportFailure ? undefined : this.message;
  }
}

/**
 * Every failure here surfaces as one message, because the server sends one.
 *
 * Expired, revoked, cancelled booking, club switched off — `resolveLink` distinguishes all
 * of them for telemetry and deliberately collapses them at the HTTP boundary. Telling an
 * anonymous caller which gate refused would confirm whether a token exists.
 *
 * Takes the real `ApiResult<T>` rather than a local structural stand-in. The stand-in was
 * `{ success: boolean; payload?: T; message?: string }`, which had three problems: it was not
 * a discriminated union so `payload` could not narrow after the guard, it structurally
 * accepted anything with those three fields so an envelope change would not break the build,
 * and it discarded `httpStatus` — the one field this surface actually needs.
 */
function unwrap<T>(result: ApiResult<T>): T {
  if (!result.success || result.payload === undefined || result.payload === null) {
    throw new AccessLinkError(
      result.message || 'This link is not valid right now.',
      result.httpStatus,
    );
  }
  return result.payload;
}

/** Exchange the token for the booking's access state. */
export async function resolveUnlockLink(token: string): Promise<LinkAccessResponse> {
  const result = await apiClient.post<LinkAccessResponse>(
    '/access/link/current',
    { token },
    { skipAuth: true },
  );
  return unwrap(result);
}

/**
 * Open the club's doors.
 *
 * No `bookingId` in the body — the server takes it from the token, and sending one would
 * imply it is negotiable. It is not.
 *
 * `retries: 0` is deliberate and load-bearing, as it is for the app's unlock. The default
 * policy retries a request that times out or whose connection drops, POSTs included; a slow
 * fan-out would then be re-sent and physically re-open doors that already opened, while the
 * member is shown a spinner. The server bounds its own fan-out well inside the client
 * timeout, so a timeout here means something is genuinely wrong — the member should be told,
 * not silently re-fired at.
 */
export async function unlockWithLink(token: string): Promise<UnlockDoorResponse> {
  const result = await apiClient.post<UnlockDoorResponse>(
    '/access/link/unlock',
    { token },
    { skipAuth: true, retries: 0 },
  );
  return unwrap(result);
}
